import * as React from 'react';
import { useEffect, useState } from 'react';
import { AlertCircle, FileText } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { FilePreview } from '@/components/ui/file-viewer';
import { LoadingSpinner } from '@/components/ui/loading-preview';
import { buildExecutionTemplateDownloadFilename } from '@/lib/execution-template-download-filename';
import {
  getLatestTemplatePreviewInfo,
  type ExecutionTemplateRow,
} from '../../services/executionTemplatesService';

export interface ExecutionTemplatePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: ExecutionTemplateRow | null;
}

const ExecutionTemplatePreviewDialog: React.FC<ExecutionTemplatePreviewDialogProps> = ({
  open,
  onOpenChange,
  row,
}) => {
  const [contentUrl, setContentUrl] = useState<string | null>(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [fileType, setFileType] = useState('');
  const [storageObjectName, setStorageObjectName] = useState<string | null>(null);
  const [subtitle, setSubtitle] = useState<string | null>(null);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;

    async function loadContent() {
      if (!open || !row) {
        setContentUrl(null);
        setHtmlContent('');
        setFileType('');
        setStorageObjectName(null);
        setSubtitle(null);
        setContentError(null);
        setIsLoadingContent(false);
        return;
      }

      setIsLoadingContent(true);
      setContentError(null);
      setContentUrl(null);
      setHtmlContent('');
      setFileType('');
      setStorageObjectName(null);
      setSubtitle(null);

      try {
        const result = await getLatestTemplatePreviewInfo(row.execution_template_id, row.content_url);
        if (cancelled) return;

        if (!result) {
          setContentError(
            'No file available yet. Generate or upload a version from the web app, or wait for processing to finish.'
          );
          return;
        }

        const ext = result.extension || 'docx';
        setFileType(ext);
        setStorageObjectName(result.fileName);
        setSubtitle(
          result.source === 'catalog'
            ? `${result.fileName} · base template`
            : `${result.fileName} · latest version`
        );

        if (result.kind === 'blob') {
          if (ext === 'html' || ext === 'txt') {
            const text = await result.blob.text();
            if (cancelled) return;
            const ou = URL.createObjectURL(result.blob);
            if (cancelled) {
              URL.revokeObjectURL(ou);
              return;
            }
            blobUrl = ou;
            setHtmlContent(text);
            setContentUrl(ou);
          } else {
            const ou = URL.createObjectURL(result.blob);
            if (cancelled) {
              URL.revokeObjectURL(ou);
              return;
            }
            blobUrl = ou;
            setContentUrl(ou);
          }
        } else if (ext === 'html' || ext === 'txt') {
          const response = await fetch(result.url);
          if (!response.ok) throw new Error(`Failed to fetch content: ${response.statusText}`);
          const text = await response.text();
          if (cancelled) return;
          setHtmlContent(text);
          setContentUrl(result.url);
        } else {
          if (cancelled) return;
          setContentUrl(result.url);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Error loading template content';
          console.error('Error loading template content:', err);
          setContentError(message);
        }
      } finally {
        if (!cancelled) setIsLoadingContent(false);
      }
    }

    void loadContent();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [open, row]);

  const previewName =
    row && storageObjectName
      ? buildExecutionTemplateDownloadFilename(row.name, storageObjectName)
      : row?.name || 'template';

  const titleBlock = (
    <div className="min-w-0 pr-2">
      <h3 className="truncate text-sm font-semibold leading-tight text-slate-900">{row?.name ?? 'Preview'}</h3>
      {subtitle ? (
        <p className="mt-0.5 truncate text-[11px] leading-snug text-slate-500" title={subtitle}>
          {subtitle}
        </p>
      ) : null}
    </div>
  );

  return (
    <Modal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title={titleBlock}
      size="full"
      className="h-[min(90vh,calc(100vh-16px))] max-h-[92vh] w-[calc(100vw-16px)] max-w-none min-h-0"
      headerClassName="shrink-0 items-center px-3 py-2"
      bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden p-0"
      overlayClassName="items-start justify-center p-2 pt-2"
      showCloseButton
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
        {isLoadingContent ? (
          <div className="flex min-h-[min(400px,55vh)] flex-1 items-center justify-center">
            <LoadingSpinner size={32} className="text-primary" />
          </div>
        ) : contentError ? (
          <div className="flex min-h-[min(400px,55vh)] flex-1 flex-col items-center justify-center px-3 py-4 text-center">
            <AlertCircle size={24} className="mb-2 text-amber-500" />
            <span className="text-sm text-slate-500">{contentError}</span>
          </div>
        ) : !contentUrl ? (
          <div className="flex min-h-[min(400px,55vh)] flex-1 flex-col items-center justify-center px-3 py-4 text-center">
            <FileText size={32} className="mb-2 text-slate-300" />
            <span className="text-sm text-slate-400">No content available</span>
          </div>
        ) : (
          <div className="min-h-0 flex-1">
            <FilePreview
              url={contentUrl}
              name={previewName}
              type={fileType}
              htmlContent={htmlContent || undefined}
              className="h-full min-h-0 w-full"
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ExecutionTemplatePreviewDialog;
